import React, { ReactNode, createContext, useReducer, useContext } from 'react';
import { CircularProgress } from '@mui/material';

import makeStyles from '@mui/styles/makeStyles';

type Action = { type: 'show' | 'hide'; message?: string };
type Dispatch = (action: Action) => void;
type State = {
  visibility: boolean;
  message?: string;
};
type LoaderProviderProperties = { children: ReactNode };

const LoaderStateContext = createContext<State | undefined>(undefined);
const LoaderDispatchContext = createContext<Dispatch | undefined>(undefined);

const loaderReducer = (state: State, action: Action) => {
  switch (action.type) {
    case 'show': {
      return {
        visibility: true,
        message: action.message,
      };
    }
    case 'hide': {
      return {
        visibility: false,
        message: '',
      };
    }
    default: {
      throw new Error(`Unhandled action type: ${action}`);
    }
  }
};

const LoaderProvider = ({ children }: LoaderProviderProperties) => {
  const useStyles = makeStyles(() => ({
    loader: {
      zIndex: 10,
      position: 'absolute',
      left: '50%',
      top: '50%',
    },
  }));
  const classes = useStyles();
  const [state, dispatch] = useReducer(loaderReducer, { visibility: false, message: '' });
  return (
    <LoaderStateContext.Provider value={state}>
      <LoaderDispatchContext.Provider value={dispatch}>
        {state.visibility && <CircularProgress size={100} className={classes.loader} />}
        {children}
      </LoaderDispatchContext.Provider>
    </LoaderStateContext.Provider>
  );
};

const useLoaderState = () => {
  const context = useContext(LoaderStateContext);
  if (context === undefined) {
    throw new Error('useLoaderState must be used within a LoaderProvider');
  }
  return context;
};

const useLoaderDispatch = () => {
  const context = useContext(LoaderDispatchContext);
  if (context === undefined) {
    throw new Error('useLoaderDispatch must be used within a LoaderProvider');
  }
  return context;
};

export { LoaderProvider, useLoaderState, useLoaderDispatch };
